/**
 * Vue « détail d'une campagne », partagée par le portail client et le portail
 * admin (§ interface unique). Elle est PRÉSENTATIONNELLE pour tout ce qui est
 * périmètre-dépendant : la campagne, ses stats et ses évaluations arrivent en
 * props, chaque portail appelant ses propres queries (org-scopées côté client,
 * `*SuperAdmin` côté admin). Les onglets référentiel, eux, interrogent
 * directement `api.referentiel.*` — ces queries portent déjà le bypass
 * `isAdmin`, il n'y a donc rien à injecter.
 *
 * Les écritures (ajout de candidats, envoi de liens, configuration du
 * référentiel, invitation de référents) sont gouvernées par `capabilities` :
 * le portail admin est en consultation.
 */
import {
  Badge,
  Button,
  Card,
  CardContent,
  PageContainer,
  StatutCampagneBadge,
  DataTable,
  useFiltresColonnesUrl,
} from "@repo/ui";
import {
  CampagneResultatsTab,
  useCampagneResultats,
  type Copsoq3EvalRow,
  type EvaluationWithScores,
} from "@repo/results";
import { useAction, useMutation } from "convex/react";
import {
  Archive,
  ArrowLeft,
  Copy,
  Link2,
  Loader2,
  Mail,
  Pencil,
  UserPlus,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { AjouterCandidatsDialog } from "./AjouterCandidatsDialog";
import {
  colonnesParcours,
  FACETTES_PARCOURS,
  RECHERCHE_PARCOURS,
  type ParcoursLigne,
} from "./colonnesParcours";
import { EnvoyerLiensDialog } from "./EnvoyerLiensDialog";
import { EntStructMatchingTab } from "./entstruct/EntStructMatchingTab";
import { NavigationCampagne } from "./NavigationCampagne";
import {
  aSynthese as calculerASynthese,
  CIBLE_DU_TEST,
  estTest,
  SECTIONS_DU_TEST,
  testsDeLaCampagne,
  type OngletCampagne,
  type SectionTest,
} from "./ongletsCampagne";
import { SyntheseTab } from "./SyntheseTab";
import { useVueCampagne } from "./useVueCampagne";
import { ReferentielMatchingTab } from "./referentiel/ReferentielMatchingTab";
import { ReferentielTab } from "./referentiel/ReferentielTab";
import { ReferentsTab } from "./referentiel/ReferentsTab";
import type {
  EvaluationFitCulturelRow,
  EvaluationPamiRow,
} from "./referentiel/ReferentielMatchingTab";
import { dateCourte, libelleComposition, pluriel } from "@repo/utils";

/**
 * Colonnes de la table Parcours : stables, calculées une fois.
 *
 * Sans colonne Type : une campagne ne porte qu'un type de parcours, la colonne
 * répéterait la même valeur sur chaque ligne — même raison que la colonne
 * `campagne`, absente elle aussi.
 */
const COLONNES_PARCOURS = colonnesParcours();

/**
 * Paramètres d'URL de la table des parcours (phase 6).
 *
 * Référence stable : recréé à chaque rendu, il ferait boucler les mémos du hook.
 */
const CHAMPS_FILTRES_PARCOURS = {
  q: { colonne: RECHERCHE_PARCOURS.colonne },
  statut: { colonne: "statut", multi: true },
  lien: { colonne: "lien", multi: true },
  epreuve: { colonne: "epreuves", multi: true },
} as const;

/** Config de la barre de filtres, stable pour la même raison. */
const BARRE_FILTRES_PARCOURS = {
  recherche: RECHERCHE_PARCOURS,
  facettes: FACETTES_PARCOURS,
};

/** Ce que le portail autorise. Tout à `false` = consultation pure. */
export type CampagneCapabilities = {
  /**
   * Envoyer les liens aux candidats depuis l'en-tête, et par quelle action :
   * org-scopée côté client, cross-org côté super-admin. `null` = bouton absent.
   */
  gererCandidats: "client" | "superAdmin" | null;
  /**
   * Ajouter des candidats, et par quelle mutation : org-scopée côté client,
   * cross-org côté super-admin. `null` = bouton absent.
   */
  ajouterCandidats: "client" | "superAdmin" | null;
  /** Configurer / figer le référentiel, inviter et relancer les référents. */
  ecritureReferentiel: boolean;
  /** Afficher le lien d'auto-inscription des candidats. */
  lienLibreService: boolean;
};

export const CAPABILITES_CLIENT: CampagneCapabilities = {
  gererCandidats: "client",
  ajouterCandidats: "client",
  ecritureReferentiel: true,
  lienLibreService: true,
};

export const CAPABILITES_ADMIN_LECTURE: CampagneCapabilities = {
  gererCandidats: null,
  ajouterCandidats: null,
  ecritureReferentiel: false,
  lienLibreService: false,
};

/**
 * Portail admin : consultation, SAUF le référentiel. Le super-admin doit pouvoir
 * configurer / dépanner un référentiel sur n'importe quelle org — c'est la seule
 * surface d'administration qui le permette, et sans elle une campagne dont le
 * référentiel est manquant ou mal configuré reste bloquée côté client (aucun
 * candidat ne peut être scoré). Les mutations `referentiel.*` portent déjà le
 * bypass `isAdmin` côté backend.
 *
 * Le super-admin peut aussi ajouter des candidats (création d'évaluations pour
 * le compte du client, facturées pareil) et leur envoyer leur lien. Le lien
 * libre-service reste un geste du client.
 */
export const CAPABILITES_ADMIN_REFERENTIEL: CampagneCapabilities = {
  gererCandidats: "superAdmin",
  ajouterCandidats: "superAdmin",
  ecritureReferentiel: true,
  lienLibreService: false,
};

/** Campagne telle que projetée par les deux queries (client / superAdmin). */
type CampagneView = {
  nom: string;
  statut: string;
  anonyme?: boolean;
  dateDebut?: number;
  dateFin?: number;
  /** Archivée = consultation seule, quelles que soient les `capabilities`. */
  archivedAt?: number;
  autoriserCreationParcoursCandidats?: boolean;
  evaluationsConfig?: { typeEval: string; template?: string }[];
};

type StatsView = {
  nbCandidats: number;
  /** Projection partagée avec `listerParcoursCampagneVue` (§Phase 5). */
  parcours: ParcoursLigne[];
};

type Props = {
  campagneId: Id<"campagnes">;
  /** `undefined` = en cours de chargement, `null` = introuvable. */
  campagne: CampagneView | null | undefined;
  stats: StatsView | null | undefined;
  /** Évaluations ent-struct de la campagne (onglet Résultats). */
  evaluations: Parameters<typeof useCampagneResultats>[0];
  evaluationsInbasket: React.ComponentProps<
    typeof CampagneResultatsTab
  >["evaluationsInbasket"];
  evaluationsPami: EvaluationPamiRow[] | undefined;
  evaluationsFitCulturel: EvaluationFitCulturelRow[] | undefined;
  /** Passations COPSOQ III terminées (onglet COPSOQ › Résultats). */
  evaluationsCopsoq3: Copsoq3EvalRow[] | undefined;
  /**
   * Référentiels de la campagne, un par cible au plus (`[]` si aucun). C'est
   * l'onglet de test ouvert qui décide lequel s'affiche.
   */
  referentiels:
    | {
        cible: "pami" | "pc-fit";
        config: {
          dimensions: { code: string; label: string }[];
          echelle:
            | { kind: "qsort"; pyramide: number[] }
            | { kind: "fourchette" };
        };
      }[]
    | undefined;
  capabilities: CampagneCapabilities;
  /** Retour arrière (liste des campagnes du portail courant). */
  retourHref: string;
  /**
   * Fil d'Ariane rendu au-dessus du titre, à la place du lien « Retour ».
   *
   * Sert le portail admin, où la navigation est hiérarchique (organisation →
   * campagne) et où un simple retour ne dit pas d'où l'on vient. Absent = lien
   * « Retour aux campagnes », comportement du portail client.
   */
  filAriane?: React.ReactNode;
  /**
   * Page de modification de la campagne : bouton « Modifier » dans l'en-tête.
   * Absent = pas de bouton (le portail admin a le sien dans `filAriane`).
   */
  modifierHref?: string;
  getParcoursHref: (parcoursId: Id<"parcoursEvaluations">) => string;
  getEvaluationHref: (
    evaluation: Pick<EvaluationWithScores, "_id" | "parcoursId">,
  ) => string;
  /** Libellé de l'action de la table Résultats (défaut : « Voir »). */
  actionLabel?: string;
  /** Ouvre la modale d'envoi de liens au montage (deep-link client). */
  ouvrirModaleEmail?: boolean;
  /** URL du portail candidat, pour le lien libre-service. */
  candidatPortailUrl?: string;
  /**
   * Jeton d'auth (`useAuthToken()`) et URL Convex de l'app, transmis à l'onglet
   * Matching pour le téléchargement du rapport fit culturel. Le PDF est servi
   * par une `httpAction`, donc par un `fetch` authentifié à la main — pas par
   * le client Convex.
   */
  token?: string | null;
  convexUrl?: string;
};

export function CampagneDetailView({
  campagneId,
  campagne,
  stats,
  evaluations,
  evaluationsInbasket,
  evaluationsPami,
  evaluationsFitCulturel,
  evaluationsCopsoq3,
  referentiels,
  capabilities: capabilitiesDeclarees,
  retourHref,
  filAriane,
  modifierHref,
  getParcoursHref,
  getEvaluationHref,
  actionLabel,
  ouvrirModaleEmail = false,
  candidatPortailUrl,
  token,
  convexUrl,
}: Props) {
  // Campagne archivée : consultation seule (résultats, rapports), sur les deux
  // portails. Le serveur refuse de toute façon ces écritures
  // (`assertCampagneModifiable`) ; ici on ne propose pas ce qui échouerait.
  const archivee = campagne?.archivedAt !== undefined;
  const capabilities = archivee ? CAPABILITES_ADMIN_LECTURE : capabilitiesDeclarees;

  // Onglet actif et section du test vivent dans l'URL, via le hook que le
  // parent utilise aussi pour ne charger que les données de la vue affichée.
  const vue = useVueCampagne();
  // Les filtres de la table vivent dans la même URL que l'onglet actif : coller
  // le lien reproduit la vue entière, filtres compris.
  const [filtresParcours, setFiltresParcours] = useFiltresColonnesUrl(
    CHAMPS_FILTRES_PARCOURS,
  );
  const [copiedLienLibreService, setCopiedLienLibreService] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [showAddCandidats, setShowAddCandidats] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(ouvrirModaleEmail);
  /**
   * Retour de l'envoi de liens (phase 8). Remplace deux `alert()` : une modale
   * native bloque la page, sort du style de l'app et n'est pas annonçable.
   * Le succès s'affiche en bandeau sous l'en-tête (la modale est fermée),
   * l'erreur reste DANS la modale, là où l'action a été lancée.
   */
  const [retourEnvoi, setRetourEnvoi] = useState<string | null>(null);
  const [erreurEnvoi, setErreurEnvoi] = useState<string | null>(null);
  const [selectedParcoursIds, setSelectedParcoursIds] = useState<
    Id<"parcoursEvaluations">[]
  >([]);
  const autoOpenDone = useRef(false);

  const envoyerLiensClient = useAction(
    api.email.envoyerLiensParcoursSelectionnes,
  );
  const envoyerLiensSuperAdmin = useAction(
    api.email.envoyerLiensParcoursSelectionnesSuperAdmin,
  );
  const envoyerLiensParcoursSelectionnes =
    capabilitiesDeclarees.gererCandidats === "superAdmin"
      ? envoyerLiensSuperAdmin
      : envoyerLiensClient;

  // Tests que la campagne fait passer : un onglet chacun, dans cet ordre.
  const tests = useMemo(
    () => testsDeLaCampagne(campagne?.evaluationsConfig ?? []),
    [campagne?.evaluationsConfig],
  );
  const aPami = tests.includes("pami");
  const aFitCulturel = tests.includes("fitculturel");
  const aEntStruct = tests.includes("entstruct");
  const avecSynthese = calculerASynthese(tests);

  // L'URL peut désigner un onglet que cette campagne n'a pas (lien collé d'une
  // autre campagne, Synthèse avec un seul test noté) : retour sur Parcours.
  const onglet: OngletCampagne = (
    vue.onglet === "synthese"
      ? avecSynthese
      : estTest(vue.onglet)
        ? tests.includes(vue.onglet)
        : true
  )
    ? vue.onglet
    : "parcours";
  const testActif = estTest(onglet) ? onglet : undefined;

  // L'onglet de test désigne le référentiel : c'est ce qui rend ses sections
  // non ambiguës sur une campagne qui porte à la fois un PAMI et un fit culturel.
  const cible = testActif ? CIBLE_DU_TEST[testActif] : undefined;
  const aReferentiel =
    cible != null && referentiels?.some((r) => r.cible === cible) === true;
  const referentielFitCulturel = referentiels?.find(
    (r) => r.cible === "pc-fit",
  )?.config;

  // Parmi les sections que le test peut porter, celles qui ont quelque chose à
  // montrer. Tant que les référentiels chargent, on ne retire rien : un lien
  // direct vers Référents ne doit pas être renvoyé sur Résultats pendant le
  // chargement. Ce filtre dépend des DONNÉES de la campagne, jamais d'une
  // sélection : les sections d'un test restent stables tant qu'on y navigue.
  const sectionDisponible = (s: SectionTest): boolean => {
    if (referentiels === undefined) return true;
    switch (s) {
      case "resultats":
        return true;
      // L'entretien structuré se compare au barème de son template : pas
      // besoin de référentiel.
      case "matching":
        return testActif === "entstruct" || aReferentiel;
      // Configurable s'il n'existe pas encore — sauf en consultation, où seul
      // l'existant compte.
      case "referentiel":
        return capabilities.ecritureReferentiel || aReferentiel;
      case "referents":
        return aReferentiel;
    }
  };
  const sections = testActif
    ? SECTIONS_DU_TEST[testActif].filter(sectionDisponible)
    : [];
  const section: SectionTest = sections.includes(vue.section)
    ? vue.section
    : "resultats";

  // Une URL qui vise une section indisponible (Référents d'un test sans
  // référentiel) est ramenée sur Résultats DANS l'URL : c'est elle que le
  // parent lit pour savoir quelles passations charger.
  useEffect(() => {
    if (testActif && referentiels !== undefined && section !== vue.section) {
      vue.choisirSection(section);
    }
  }, [testActif, referentiels, section, vue]);

  // RATTRAPAGE : le référentiel fit-culturel n'a plus d'étape d'activation — il
  // est posé à la création de la campagne. Les campagnes créées AVANT ce
  // changement n'en ont pas et n'auraient plus aucun moyen d'en obtenir un :
  // on le pose à l'ouverture, quel que soit l'onglet. Idempotent côté serveur,
  // et une seule tentative par montage (`ref`) pour qu'un échec ne devienne pas
  // une boucle d'écriture.
  const assurerReferentielFitCulturel = useMutation(
    api.referentiel.campagne.assurerReferentielFitCulturel,
  );
  const rattrapageTente = useRef(false);
  useEffect(() => {
    if (rattrapageTente.current) return;
    if (!capabilities.ecritureReferentiel) return;
    if (!aFitCulturel) return;
    // `undefined` = référentiels en cours de chargement : attendre, sinon on
    // écrirait sur une campagne qui en a peut-être déjà un.
    if (referentiels === undefined) return;
    if (referentiels.some((r) => r.cible === "pc-fit")) return;
    rattrapageTente.current = true;
    void assurerReferentielFitCulturel({ campagneId });
  }, [
    assurerReferentielFitCulturel,
    aFitCulturel,
    campagneId,
    capabilities.ecritureReferentiel,
    referentiels,
  ]);

  useEffect(() => {
    if (
      stats &&
      showEmailModal &&
      !autoOpenDone.current &&
      selectedParcoursIds.length === 0
    ) {
      autoOpenDone.current = true;
      setSelectedParcoursIds(
        stats.parcours.filter((p) => !p.lienEnvoye).map((p) => p._id),
      );
    }
  }, [stats, showEmailModal, selectedParcoursIds.length]);

  const toggleParcours = (parcoursId: Id<"parcoursEvaluations">) => {
    setSelectedParcoursIds((prev) =>
      prev.includes(parcoursId)
        ? prev.filter((id) => id !== parcoursId)
        : [...prev, parcoursId],
    );
  };

  const toggleAllParcours = () => {
    if (!stats) return;
    if (selectedParcoursIds.length === stats.parcours.length) {
      setSelectedParcoursIds([]);
    } else {
      setSelectedParcoursIds(stats.parcours.map((p) => p._id));
    }
  };

  const handleSendEmails = async () => {
    if (selectedParcoursIds.length === 0) return;
    setSendingEmails(true);
    try {
      const res = await envoyerLiensParcoursSelectionnes({
        campagneId,
        parcoursIds: selectedParcoursIds,
      });
      setErreurEnvoi(null);
      setRetourEnvoi(res.message);
      setShowEmailModal(false);
      setSelectedParcoursIds([]);
    } catch (err) {
      setErreurEnvoi(err instanceof Error ? err.message : String(err));
    } finally {
      setSendingEmails(false);
    }
  };

  const { evaluationsWithScores, allCompetences, handleExportCSV } =
    useCampagneResultats(evaluations, campagne?.nom);

  // --- Loading ---

  if (campagne === undefined || stats === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (campagne === null || stats === null) {
    return (
      <div className="min-h-screen flex flex-col items-start">
        <PageContainer>
          <Link
            to={retourHref}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux campagnes
          </Link>
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">Campagne introuvable</p>
            </CardContent>
          </Card>
        </PageContainer>
      </div>
    );
  }

  const lienLibreService = `${candidatPortailUrl}/c/${campagneId}/new`;
  // Archivée : le serveur refuse la modification (`assertCampagneModifiable`).
  const lienModifier = archivee ? undefined : modifierHref;

  return (
    <div className="min-h-screen flex flex-col items-start">
      <PageContainer>
        {/* Header */}
        <div className="mb-6">
          {filAriane ?? (
            <Link
              to={retourHref}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour aux campagnes
            </Link>
          )}
          {archivee && (
            <div
              role="status"
              className="mb-4 flex items-center gap-3 rounded-surface border border-border bg-muted/40 px-4 py-3 text-sm"
            >
              <Archive className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span>
                Campagne archivée le {dateCourte(campagne.archivedAt!)} :
                consultation seule. Les liens candidat et évaluateur sont
                fermés ; les résultats restent disponibles.
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{campagne.nom}</h1>
              <div className="flex items-center gap-3 mt-2">
                <StatutCampagneBadge statut={campagne.statut} />
                {/* La composition vit ici, pas dans la table : elle est la
                    même pour tous les parcours de la campagne. */}
                <Badge variant="outline">
                  {libelleComposition(campagne.evaluationsConfig ?? [])}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {pluriel(stats.nbCandidats, "candidat")}
                </span>
                <span className="text-sm text-muted-foreground">
                  {stats.parcours.filter((p) => p.lienEnvoye).length}/
                  {stats.nbCandidats} liens envoyés
                </span>
                {campagne.dateDebut && (
                  <span className="text-sm text-muted-foreground">
                    Début : {dateCourte(campagne.dateDebut)}
                  </span>
                )}
                {campagne.dateFin && (
                  <span className="text-sm text-muted-foreground">
                    Fin : {dateCourte(campagne.dateFin)}
                  </span>
                )}
              </div>
            </div>
            {(lienModifier ||
              capabilities.gererCandidats ||
              capabilities.ajouterCandidats) && (
              <div className="flex items-center gap-2">
                {lienModifier && (
                  <Link to={lienModifier}>
                    <Button variant="outline" size="sm">
                      <Pencil className="h-4 w-4 mr-2" />
                      Modifier
                    </Button>
                  </Link>
                )}
                {capabilities.ajouterCandidats && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddCandidats(true)}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Ajouter des candidats
                  </Button>
                )}
                {capabilities.gererCandidats && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={campagne.anonyme || stats.parcours.length === 0}
                  onClick={() => {
                    setSelectedParcoursIds(
                      stats.parcours
                        .filter((p) => !p.lienEnvoye)
                        .map((p) => p._id),
                    );
                    setErreurEnvoi(null);
                    setShowEmailModal(true);
                  }}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer les liens
                </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Retour d'envoi de liens — succès seulement : l'erreur reste dans la
            modale, qui ne s'est pas fermée. */}
        {retourEnvoi && (
          <div
            role="status"
            className="mb-6 flex items-center gap-3 rounded-surface border border-etat-fait-trait bg-etat-fait-surface px-4 py-3 text-sm"
          >
            <Mail className="h-4 w-4 shrink-0 text-etat-fait" />
            <span className="flex-1 text-etat-fait-contenu">{retourEnvoi}</span>
            <button
              type="button"
              aria-label="Masquer ce message"
              className="shrink-0 text-etat-fait hover:text-etat-fait-contenu"
              onClick={() => setRetourEnvoi(null)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Lien libre-service */}
        {capabilities.lienLibreService &&
          campagne.autoriserCreationParcoursCandidats && (
            <div className="mb-6 flex items-center gap-3 rounded-surface border border-etat-info-trait bg-etat-info-surface px-4 py-3 text-sm">
              <Link2 className="h-4 w-4 shrink-0 text-etat-info" />
              <span className="text-etat-info-contenu font-medium">
                Lien candidats :
              </span>
              <span className="font-mono text-etat-info-contenu truncate flex-1">
                {lienLibreService}
              </span>
              <button
                type="button"
                aria-label="Copier le lien candidats"
                className="shrink-0 text-etat-info hover:text-etat-info-contenu"
                onClick={() => {
                  void navigator.clipboard.writeText(lienLibreService);
                  setCopiedLienLibreService(true);
                  setTimeout(() => setCopiedLienLibreService(false), 2000);
                }}
              >
                {copiedLienLibreService ? (
                  <span className="text-xs text-etat-fait font-medium">
                    Copié !
                  </span>
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          )}

        <NavigationCampagne
          tests={tests}
          avecSynthese={avecSynthese}
          onglet={onglet}
          sections={sections}
          section={section}
          onChoisirOnglet={vue.choisirOnglet}
          onChoisirSection={vue.choisirSection}
        />

        {/* Synthèse — récapitulatif transverse, un score global par test. */}
        {onglet === "synthese" && (
          <SyntheseTab
            campagneId={campagneId}
            parcours={stats.parcours}
            evaluations={evaluations}
            evaluationsPami={evaluationsPami}
            evaluationsFitCulturel={evaluationsFitCulturel}
            aPami={aPami}
            aFitCulturel={aFitCulturel}
            aEntStruct={aEntStruct}
          />
        )}

        {/* Parcours */}
        {onglet === "parcours" && (
          <DataTable
            colonnes={COLONNES_PARCOURS}
            donnees={stats.parcours}
            cleColonnes="campagne-parcours"
            barreFiltres={BARRE_FILTRES_PARCOURS}
            filtres={filtresParcours}
            onFiltresChange={setFiltresParcours}
            hrefLigne={(ligne) => getParcoursHref(ligne._id)}
            vide={{
              titre: "Aucun parcours dans cette campagne",
              description: capabilities.ajouterCandidats
                ? "Ajoutez des candidats pour démarrer."
                : undefined,
            }}
            videFiltre={{
              titre: "Aucun parcours ne correspond",
              description: "Modifiez ou réinitialisez les filtres.",
              action: (
                <Button
                  variant="outline"
                  onClick={() => setFiltresParcours([])}
                >
                  Effacer les filtres
                </Button>
              ),
            }}
          />
        )}

        {/* Test › Résultats */}
        {testActif && section === "resultats" && (
          <CampagneResultatsTab
            testActif={testActif}
            evaluationsInbasket={evaluationsInbasket}
            evaluationsPami={evaluationsPami}
            evaluationsFitCulturel={evaluationsFitCulturel}
            itemsFitCulturel={referentielFitCulturel?.dimensions}
            nbNiveauxFitCulturel={
              referentielFitCulturel?.echelle.kind === "qsort"
                ? referentielFitCulturel.echelle.pyramide.length
                : undefined
            }
            evaluationsCopsoq3={evaluationsCopsoq3}
            campagneNom={campagne.nom}
            evaluations={evaluations}
            evaluationsWithScores={evaluationsWithScores}
            allCompetences={allCompetences}
            onExportCSV={handleExportCSV}
            getEvaluationHref={getEvaluationHref}
            actionLabel={actionLabel}
          />
        )}

        {/* Entretien structuré › Matching — contre le barème de son template. */}
        {testActif === "entstruct" && section === "matching" && (
          <EntStructMatchingTab evaluations={evaluations} />
        )}

        {/* PAMI / Fit culturel › Matching — candidat ↔ référentiel */}
        {section === "matching" && aReferentiel && cible && (
          <ReferentielMatchingTab
            campagneId={campagneId}
            cible={cible}
            campagneNom={campagne.nom}
            // Un référentiel ne compare QUE les passations de sa cible : lui
            // passer les deux jeux ferait cohabiter dans un même classement des
            // candidats notés sur des échelles différentes.
            evaluationsPami={cible === "pami" ? evaluationsPami : []}
            evaluationsFitCulturel={
              cible === "pc-fit" ? evaluationsFitCulturel : []
            }
            token={token}
            convexUrl={convexUrl}
          />
        )}

        {/* PAMI / Fit culturel › Référentiel */}
        {section === "referentiel" && cible && (
          <ReferentielTab
            campagneId={campagneId}
            cible={cible}
            lectureSeule={!capabilities.ecritureReferentiel}
          />
        )}

        {/* PAMI / Fit culturel › Référents — progression, contributions */}
        {section === "referents" && aReferentiel && cible && (
          <ReferentsTab
            campagneId={campagneId}
            cible={cible}
            lectureSeule={!capabilities.ecritureReferentiel}
          />
        )}
      </PageContainer>

      {capabilities.ajouterCandidats && showAddCandidats && (
        <AjouterCandidatsDialog
          isOpen={true}
          onClose={() => setShowAddCandidats(false)}
          campagneId={campagneId}
          campagneNom={campagne.nom}
          anonyme={campagne.anonyme}
          superAdmin={capabilities.ajouterCandidats === "superAdmin"}
        />
      )}

      {capabilities.gererCandidats && (
        <EnvoyerLiensDialog
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setSelectedParcoursIds([]);
            setErreurEnvoi(null);
          }}
          erreur={erreurEnvoi}
          parcours={stats.parcours}
          selectedIds={selectedParcoursIds}
          sendingEmails={sendingEmails}
          onToggle={toggleParcours}
          onToggleAll={toggleAllParcours}
          onSend={handleSendEmails}
        />
      )}
    </div>
  );
}
